import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useLang } from "@/i18n/LangContext";
import { Send, Instagram, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export const ContactSection = ({ settings }) => {
  const { t } = useLang();
  const instagramUrl = settings?.instagram_url || "https://www.instagram.com/purple_ink_studio/";
  const instagramHandle = settings?.instagram_handle || "@purple_ink_studio";
  const location = settings?.location || "Bucharest, Sector 3";
  const openHours = settings?.open_hours || "Tue - Sat, 11:00 - 20:00";

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      if (response.status === 200) {
        toast.success("Message sent! We'll get back to you soon.");
        setForm({ name: "", email: "", message: "" });
      }
    } catch (err) {
      console.error("Contact form error:", err);
      toast.error("Failed to send message. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 relative" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16">
          <div>
            <p
              className="text-xs tracking-[0.3em] uppercase font-bold text-pink-500 mb-3"
              style={{ fontFamily: "'Cinzel Decorative', serif" }}
            >
              {t("contact_subtitle")}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-8" data-testid="contact-title">
              {t("contact_ready")}
              <br />
              <span className="text-pink-500 neon-text-pink">{t("contact_next_piece")}</span>
            </h2>
            <p className="text-base text-neutral-400 leading-relaxed mb-12">
              {t("contact_body")}
            </p>

            <div className="space-y-6">
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 text-neutral-300 hover:text-purple-400 transition-colors duration-200 group"
                data-testid="contact-instagram"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg group-hover:bg-purple-500/10 transition-colors duration-300">
                  <Instagram className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold" data-testid="instagram-handle">{instagramHandle}</p>
                  <p className="text-xs text-neutral-500">{t("contact_book_dm")}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 text-neutral-300" data-testid="contact-location">
                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t("contact_studio_loc")}</p>
                  <p className="text-xs text-neutral-500" data-testid="location-text">{location}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-neutral-300" data-testid="contact-hours">
                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t("contact_open_hours")}</p>
                  <p className="text-xs text-neutral-500" data-testid="hours-text">{openHours}</p>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            <Input
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder={t("contact_name")}
              className="bg-black/50 border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 text-white placeholder:text-neutral-600 h-12 rounded-lg"
              data-testid="contact-name-input"
            />
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              placeholder={t("contact_email")}
              className="bg-black/50 border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 text-white placeholder:text-neutral-600 h-12 rounded-lg"
              data-testid="contact-email-input"
            />
            <Textarea
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder={t("contact_message")}
              className="bg-black/50 border-white/10 focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 text-white placeholder:text-neutral-600 min-h-[140px] rounded-lg"
              data-testid="contact-message-input"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-4 border border-purple-400 shadow-[0_0_20px_rgba(192,38,211,0.3)] hover:shadow-[0_0_30px_rgba(192,38,211,0.6)] transition-all duration-300 uppercase tracking-widest text-sm h-12"
              data-testid="contact-submit-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? t("contact_sending") : t("contact_send")}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
